import {
  Component,
  EventEmitter,
  Injector,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { BaseComponent } from 'src/app/common/base/base.component';
import { PrivilegeEnum } from 'src/app/common/enumerations/privilege-enum';
import { Role } from 'src/app/components/catalogs/roles/services/role';
import { ProcedureObligationType } from 'src/app/components/procedure/enums/procedure-obligation-type.enum';
import { ProcedureMaster } from 'src/app/components/procedure/models/procedure-master.model';
import { ProcedureTraining } from 'src/app/components/procedure/models/procedure-training.model';
import { Procedure } from 'src/app/components/procedure/models/procedure.model';
import { YesNoDialogComponent } from 'src/app/controls/yes-no-dialog/yes-no-dialog.component';
import { utils } from 'src/app/modules/libs/utils';

@Component({
  selector: 'app-role-training',
  templateUrl: './role-training.component.html',
  styleUrls: ['./role-training.component.scss'],
})
export class RoleTrainingComponent extends BaseComponent implements OnChanges {
  @Input() role?: Role | null;
  @Input() displayedRole?: Role | null;
  @Input() adding = false;
  @Input() modifying = false;
  @Input() active = false;
  @Input() procedureTrainings?: ProcedureTraining[] = [];
  @Input() procedures?: Procedure[] = [];

  @Output() trainingsChange = new EventEmitter<ProcedureTraining[]>();
  @Output() dirty = new EventEmitter<void>();

  trainingColumns: string[] = ['procedure', 'obligation', 'delete'];
  procedureTrainingsRole: ProcedureTraining[] = [];
  procedureTrainingsDS = new MatTableDataSource<ProcedureTraining>([]);
  selectedProcedures?: (Procedure | undefined)[] = [];
  procedureTrainingAdmin?: boolean | null = false;

  constructor(protected override injector: Injector) {
    super(injector);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['role'] ||
      changes['displayedRole'] ||
      changes['procedureTrainings'] ||
      changes['procedures']
    ) {
      this.setTrainingDS();
    }
  }

  getTrainingsForSave(): ProcedureTraining[] {
    const trainings = utils.cloneDeep(this.procedureTrainingsRole);
    if (trainings)
      trainings.map((training) => {
        if (training.procedureMaster)
          training.procedureMaster.procedure = undefined;
        training.role = undefined;
      });
    return trainings ?? [];
  }

  createTraining(procedure: Procedure) {
    const newProcedureTraining = new ProcedureTraining();
    newProcedureTraining.procedureMasterID = procedure.procedureMasterID;
    newProcedureTraining.roleID = this.displayedRole?.id;
    newProcedureTraining.obligationType =
      ProcedureObligationType.RequiredFollowUp;
    newProcedureTraining.procedureMaster = new ProcedureMaster();
    if (newProcedureTraining.procedureMaster) {
      newProcedureTraining.procedureMaster.id =
        procedure.procedureMasterID ?? 0;
      newProcedureTraining.procedureMaster.procedure = procedure;
    }
    this.procedureTrainingsRole?.push(newProcedureTraining);
    this.sortTrainings();
    this.refreshDataSource();
    this.markDirty();
  }

  updateTraining() {
    this.markDirty();
  }

  deleteTraining(procedureTraining: ProcedureTraining) {
    this.dialog
      .open(YesNoDialogComponent, {
        minWidth: '400px',
        data: {
          message: this.getMessage(
            'ALSPC_RolesCatalog_AreYouSureYouWantToDeleteTheProcedure',
          ).description,
        },
      })
      .afterClosed()
      .subscribe((data) => {
        if (data) {
          const index = this.procedureTrainingsRole?.indexOf(procedureTraining) ?? -1;
          if (index >= 0) {
            this.procedureTrainingsRole?.splice(index, 1);
            this.refreshDataSource();
            this.markDirty();
          }
        }
      });
  }

  private setTrainingDS() {
    const role: Role | null | undefined = utils.cloneDeep(this.displayedRole);
    if (role?.id) {
      this.procedureTrainingAdmin =
        this.hasPrivilege(PrivilegeEnum.ALSProcedureTrainingAdmin) ||
        role?.roleALSPCSupervisors?.some(
          (x) => x.userID == this.currentUser?.id,
        ) ||
        role?.roleALSPCDesignees?.some(
          (x) => x.userID == this.currentUser?.id,
        ) ||
        role?.aplGroupRole?.aplGroup?.aplGroupManagers?.some(
          (x) => x?.userID === this.currentUser?.id,
        ) ||
        role?.aplGroupRole?.aplGroup?.aplGroupMaster?.aplCategoryMaster?.aplCategory?.aplCategoryManagers?.some(
          (x) => x?.userID === this.currentUser?.id,
        );

      this.procedureTrainingsRole = utils.cloneDeep(
        this.displayedRole?.procedureTrainings ?? [],
      );

      this.procedureTrainingsRole?.forEach((training) =>
        this.hydrateProcedureMaster(training),
      );

      this.sortTrainings();
      this.refreshDataSource();
    } else {
      this.procedureTrainingAdmin = this.hasPrivilege(
        PrivilegeEnum.ALSProcedureTrainingAdmin,
      );
      this.procedureTrainingsRole = [];
      this.refreshDataSource();
    }
  }

  private sortTrainings(): void {
    this.procedureTrainingsRole?.sort((a, b) =>
      utils.sort(
        a.procedureMaster?.procedure?.procedureNumber,
        b.procedureMaster?.procedure?.procedureNumber,
        true,
      ),
    );
    this.selectedProcedures = this.procedureTrainingsRole?.map(
      (x) => x.procedureMaster?.procedure,
    );
  }

  private hydrateProcedureMaster(training: ProcedureTraining): void {
    if (training.procedureMaster?.procedure) return;

    const procedureMasterID =
      training.procedureMasterID ?? training.procedureMaster?.id;
    const sourceTraining = (this.procedureTrainings ?? []).find(
      (source) => source.procedureMasterID == procedureMasterID,
    );
    const sourceProcedure = (this.procedures ?? []).find(
      (procedure) => procedure.procedureMasterID == procedureMasterID,
    );

    if (sourceTraining?.procedureMaster) {
      training.procedureMaster = utils.cloneDeep(sourceTraining.procedureMaster);
    }

    if (!training.procedureMaster) {
      training.procedureMaster = new ProcedureMaster();
      training.procedureMaster.id = procedureMasterID ?? 0;
    }

    if (!training.procedureMaster.procedure && sourceProcedure) {
      training.procedureMaster.procedure = sourceProcedure;
    }
  }

  private refreshDataSource(): void {
    this.procedureTrainingsDS = new MatTableDataSource(
      this.procedureTrainingsRole,
    );
    this.syncDisplayedRoleTrainings();
    this.emitTrainings();
  }

  private markDirty(): void {
    this.syncDisplayedRoleTrainings();
    this.emitTrainings();
    this.dirty.emit();
  }

  private emitTrainings(): void {
    this.trainingsChange.emit(this.procedureTrainingsRole ?? []);
  }

  private syncDisplayedRoleTrainings(): void {
    if (this.displayedRole) {
      this.displayedRole.procedureTrainings = this.procedureTrainingsRole ?? [];
    }
  }
}
